import { EditIcon } from '@wonderschool/common-base-ui';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface StepSwitchGuideProps {
  onContinue: (sourceId: string) => void;
  onEnterByHand: () => void;
}

/**
 * Source picker + export guide. Styling and copy ported from the
 * `ccms-student-list` prototype (product-docs): branded logo cards, navy
 * headings, and per-source export instructions.
 *
 * @handoff Why: the hard part of switching isn't our importer, it's getting
 * data OUT of the old tool in a usable shape. Naming the source lets us give
 * exact export steps and pre-tune column detection to that tool's headers.
 * @handoff Next: SOURCES is an inline mock. Verify each tool's export steps
 * against its current UI; wire per-source column mapping into parseCsv
 * (types.ts). Logos live in /public/source-logos.
 */
type SourceGroup = 'software' | 'spreadsheet' | 'other';

type SourceOption = {
  id: string;
  group: SourceGroup;
  name: string;
  tagline: string;
  logoSrc?: string;
  // Height class for the logo image (some wordmarks read large at the default).
  logoClass?: string;
  mark: string;
  markBg: string;
  markFg: string;
  // Optional icon shown in the tile instead of the text mark (for cards
  // without a brand logo, e.g. "Type them in myself").
  icon?: ReactNode;
  title: string;
  steps: ReactNode[];
  // Link to the tool's own export help page. @handoff: verify each deep link.
  helpUrl?: string;
  helpLabel?: string;
  // Parked out of the shippable v1 (not being QA'd yet). Kept here so re-enabling
  // is a one-line change; just remove the flag. Applies to Brightwheel, Procare,
  // Playground for now.
  parked?: boolean;
};

const SOURCES: SourceOption[] = [
  {
    id: 'brightwheel',
    group: 'software',
    parked: true,
    name: 'Brightwheel',
    tagline: 'Export your roster as CSV',
    logoSrc: '/source-logos/brightwheel.png',
    mark: 'bw',
    markBg: 'bg-blue-200',
    markFg: 'text-blue-950',
    title: 'Get your list from Brightwheel',
    helpUrl: 'https://help.mybrightwheel.com/en/articles/6159026-export-student-rosters-from-other-systems',
    helpLabel: "See Brightwheel's export guide",
    steps: [
      <>
        In Brightwheel, open <strong>Students</strong>.
      </>,
      <>
        Click <strong>Export Roster</strong>. Brightwheel emails you the file.
      </>,
      <>Open the email, save the file, then add it on the next step.</>,
    ],
  },
  {
    id: 'procare',
    group: 'software',
    parked: true,
    name: 'Procare',
    tagline: 'Run a Students & Family report',
    logoSrc: '/source-logos/procare.png',
    mark: 'P',
    markBg: 'bg-red-200',
    markFg: 'text-red-950',
    title: 'Get your list from Procare',
    helpUrl: 'https://www.procaresupport.com',
    helpLabel: "See Procare's report guide",
    steps: [
      <>
        In Procare, open <strong>Reports</strong>.
      </>,
      <>
        Run the <strong>Students and Family</strong> report and save it as a CSV.
      </>,
      <>Add the file on the next step.</>,
    ],
  },
  {
    id: 'playground',
    group: 'software',
    parked: true,
    name: 'Playground',
    tagline: 'Download your student view',
    logoSrc: '/source-logos/playground.png',
    logoClass: 'h-8',
    mark: 'pg',
    markBg: 'bg-green-200',
    markFg: 'text-green-950',
    title: 'Get your list from Playground',
    helpUrl: 'https://help.tryplayground.com/en/articles/5952521-migrate-data-roster-exports-from-other-systems',
    helpLabel: "See Playground's export guide",
    steps: [
      <>
        Open your <strong>Students</strong> list in Playground.
      </>,
      <>
        Click <strong>Download</strong> and pick <strong>All columns</strong>.
      </>,
      <>Save the file, then add it on the next step.</>,
    ],
  },
  {
    id: 'sheets',
    group: 'spreadsheet',
    name: 'Google Sheets',
    tagline: 'Copy or download from your sheet',
    logoSrc: '/source-logos/google-sheets.png',
    mark: 'GS',
    markBg: 'bg-green-200',
    markFg: 'text-green-950',
    title: 'Get your list from Google Sheets',
    helpUrl: 'https://support.google.com/docs/answer/49114',
    helpLabel: 'See how to download from Google Sheets',
    steps: [
      <>Open your list in Google Sheets.</>,
      <>
        Click <strong>File</strong>, then <strong>Download</strong>, then <strong>CSV</strong>.
      </>,
      <>Add the file on the next step.</>,
    ],
  },
  {
    id: 'excel',
    group: 'spreadsheet',
    name: 'Microsoft Excel',
    tagline: 'Copy or save from your workbook',
    logoSrc: '/source-logos/excel.png',
    mark: 'X',
    markBg: 'bg-green-200',
    markFg: 'text-green-950',
    title: 'Get your list from Excel',
    helpUrl:
      'https://support.microsoft.com/en-us/office/import-or-export-text-txt-or-csv-files-5250ac4c-663c-47ce-937b-339e391393ba',
    helpLabel: 'See how to save a CSV from Excel',
    steps: [
      <>Open your list in Excel.</>,
      <>
        Click <strong>File</strong>, then <strong>Save As</strong>, and pick <strong>CSV</strong>.
      </>,
      <>Add the file on the next step.</>,
    ],
  },
  {
    id: 'other',
    group: 'other',
    name: 'Type them in myself',
    tagline: 'Add students by hand, no file needed',
    mark: '+',
    markBg: 'bg-purple-200',
    markFg: 'text-purple-950',
    icon: <EditIcon className="h-6 w-6 text-purple-950" />,
    title: 'Type your students in by hand',
    steps: [
      <>You'll add your students one at a time on the next step.</>,
      <>For each child, add their name, a parent's email, and their room.</>,
      <>No file needed.</>,
    ],
  },
];

const GROUPS: { id: SourceGroup; label: string }[] = [
  { id: 'software', label: 'From other child care software' },
  { id: 'spreadsheet', label: 'From a spreadsheet' },
  { id: 'other', label: 'Other' },
];

function SourceLogo({ source }: { source: SourceOption }) {
  const [imgFailed, setImgFailed] = useState(false);
  if (source.logoSrc && !imgFailed) {
    return (
      <div className="flex h-12 items-center justify-center">
        <img
          src={source.logoSrc}
          alt={`${source.name} logo`}
          className={`w-auto object-contain ${source.logoClass ?? 'h-10'}`}
          onError={() => setImgFailed(true)}
        />
      </div>
    );
  }
  return (
    <div className="flex h-12 items-center justify-center">
      <div
        className={`flex h-11 w-11 items-center justify-center rounded-md text-lg font-semibold ${source.markBg} ${source.markFg}`}
      >
        {source.icon ?? <span className="tracking-wide">{source.mark}</span>}
      </div>
    </div>
  );
}

export function getSourceGuide(id?: string): SourceOption | undefined {
  return SOURCES.find((s) => s.id === id);
}

/** The blue "how to export from X" callout. Shown on the upload step. */
export function SourceInstructions({ source }: { source: SourceOption }) {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-col gap-2 rounded-lg border border-blue-200 bg-blue-50 p-4"
      data-testid="af-source-instructions"
    >
      <div className="text-sm font-semibold text-navy-950">{t(source.title)}</div>
      <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm text-gray-800">
        {source.steps.map((step, i) => (
          <li key={i}>{step}</li>
        ))}
      </ol>
      {source.helpUrl && (
        <a
          href={source.helpUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-blue-900 hover:underline"
        >
          {source.helpLabel ? t(source.helpLabel) : t('Get more help')} →
        </a>
      )}
    </div>
  );
}

export function StepSwitchGuide({ onContinue, onEnterByHand }: StepSwitchGuideProps) {
  const { t } = useTranslation();
  // Picking a card advances immediately. Tool/spreadsheet sources go to the
  // upload step (which shows that tool's export instructions above the
  // dropzone); "Type them in myself" branches off.
  function handlePick(s: SourceOption) {
    if (s.id === 'other') onEnterByHand();
    else onContinue(s.id);
  }

  return (
    <section className="space-y-6 rounded-lg border border-gray-200 bg-white p-6" data-testid="af-step-switch">
      <div>
        <h2 className="text-2xl font-bold text-navy-950">{t('addFamilies.sourceGuide.title')}</h2>
        <p className="mt-1 text-sm text-gray-600">
          {t(
            "Choose where your students are today. We'll show you how to bring them in, then you can review everyone before they're added."
          )}
        </p>
      </div>

      {GROUPS.map((g) => {
        const items = SOURCES.filter((s) => s.group === g.id && !s.parked);
        if (items.length === 0) return null;
        return (
          <div key={g.id} className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-600">{t(g.label)}</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => handlePick(s)}
                  className="flex min-h-[132px] flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white p-6 text-center transition hover:border-blue-400 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
                >
                  <SourceLogo source={s} />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm font-semibold text-navy-950">{t(s.name)}</span>
                    {s.tagline && <span className="text-xs font-normal text-gray-600">{t(s.tagline)}</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}

      <p className="text-xs text-gray-600">
        {t(
          'Brightwheel, Procare, Playground, Google Sheets, and Microsoft Excel are trademarks of their respective owners and are not affiliated with Wonderschool.'
        )}
      </p>
    </section>
  );
}
